import {
  Alert,
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material'
import {
  DeleteOutlineOutlined as DeleteOutlineOutlinedIcon,
  ModeEditOutlineOutlined as ModeEditOutlineOutlinedIcon,
  Clear as ClearIcon,
  SaveOutlined as SaveOutlinedIcon,
} from '@mui/icons-material'
import AddIcon from 'components/add-icon'
import { fetchCropsWithCropVariety } from 'api/crop'
import {
  addCropVariety,
  deleteCropVariety,
  editCropVariety,
} from 'api/crop-variety'
import { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import colors from 'common/colors'
import { unitConvert } from '@farmtrx/units'
import { getTranslatedName } from 'common/misc'

const iconButtonStyle = {
  '&:disabled svg path': {
    color: '#ECE9F1',
  },
}

export default function CropsComponent() {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const permissions = useSelector((state) => state.account.permissions)
  const cropConstants = useSelector(
    (state) => state.crop['cropConstantsCollection'],
  )
  const [sortedCropConstants, setSortedCropConstants] = useState([])
  const cropVarieties = useSelector((state) => state.cropVariety['collection'])
  const [rows, setRows] = useState(cropVarieties)
  const testWeightUnit = useSelector(
    (state) => state.account.singleton.prefs?.units?.testweight_in,
  )

  const [canEdit, setCanEdit] = useState(false)
  const [rowSelected, setSelectedRow] = useState()
  const [addButtonClicked, toggleAddRow] = useState(false)
  const [errors, setErrors] = useState({})

  const [editObj, setEditObj] = useState({})
  const [disableDeleteItem, setDisableDeleteItem] = useState(false)

  useEffect(() => {
    setCanEdit(permissions.includes('farm:write'))
  }, [permissions])

  useEffect(() => {
    if (cropConstants.length > 0) {
      const _sortedCropConstants = cropConstants.sort((a, b) => {
        const nameA = a.name.toLowerCase()
        const nameB = b.name.toLowerCase()
        if (nameA > nameB) {
          return 1
        } else if (nameA < nameB) {
          return -1
        }
        return 0
      })
      setSortedCropConstants(_sortedCropConstants)
    }
  }, [cropConstants])

  useEffect(() => {
    if (cropVarieties.length) {
      setRows(cropVarieties)
    } else {
      setRows([])
    }
  }, [cropVarieties])

  const enableSelectedRow = useCallback(
    (row) => {
      if (disableDeleteItem) {
        setDisableDeleteItem(false)
      }
      row.testWeightKgHl = unitConvert.convertTestweight(
        row.testWeightKgHl,
        unitConvert.KG_HL,
        testWeightUnit,
      )
      setEditObj(row)
      toggleAddRow(false)
      setSelectedRow(row && row.id)
    },
    [rows, disableDeleteItem],
  )

  const addEmptyRow = useCallback(
    (e) => {
      if (disableDeleteItem) {
        setDisableDeleteItem(false)
      }
      if (addButtonClicked) {
        e.preventDefault()
      } else {
        toggleAddRow(true)
        let id = uuidv4()
        setSelectedRow(id)
        const newCrop = getNewCrop(sortedCropConstants[0].id)
        setRows([
          {
            id: id,
            manufacturer: '',
            ...newCrop,
          },
          ...cropVarieties,
        ])
        setEditObj({ id })
      }
    },
    [cropVarieties, sortedCropConstants, disableDeleteItem, addButtonClicked],
  )

  const saveData = useCallback(
    async (row) => {
      row.testWeightKgHl = unitConvert.convertTestweight(
        row.testWeightKgHl,
        testWeightUnit,
        unitConvert.KG_HL,
      )
      if (addButtonClicked) {
        await dispatch(addCropVariety(row))
        setSelectedRow(-1)
      } else {
        await dispatch(editCropVariety(row.id, row))
        setSelectedRow(-1)
      }
      toggleAddRow(false)
      await dispatch(fetchCropsWithCropVariety())
    },
    [cropVarieties, sortedCropConstants, addButtonClicked],
  )

  const deleteVariety = useCallback(
    (id) => {
      if (cropVarieties?.length > 1) {
        dispatch(deleteCropVariety(id))
        toggleAddRow(false)
      } else {
        setDisableDeleteItem(true)
      }
    },
    [cropVarieties, sortedCropConstants, addButtonClicked, disableDeleteItem],
  )

  const handleInputChange = useCallback((e, row) => {
    const { name, value } = e.target
    const { id } = row
    const newRows = rows.map((row) => {
      if (row.id === id) {
        if (name === 'cropId') {
          let valuesToChange = getNewCrop(value)
          //remove name so that we don't change it
          delete valuesToChange.name
          return { ...row, ...valuesToChange }
        } else if (name === 'marketMoisture') {
          return { ...row, [name]: value }
        } else {
          return { ...row, [name]: value }
        }
      }
      return row
    })
    validate({ [name]: value })
    setRows(newRows)
  })

  const handleOnBlur = useCallback((e, row) => {
    const { name, value } = e.target
    const { id } = row
    const newRows = rows.map((row) => {
      if (row.id === id) {
        if (name === 'cropId') {
          let valuesToChange = getNewCrop(value)
          return { ...row, ...valuesToChange }
        } else {
          if (name === 'testWeightKgHl') {
            return {
              ...row,
              [name]: value,
            }
          }
          return { ...row, [name]: value }
        }
      }
      return row
    })
    validate({ [name]: value })
    setRows(newRows)
  })

  function getNewCrop(id) {
    setErrors({})
    const obj = cropConstants.filter((item) => item.id === parseInt(id))[0]
    let valuesToChange = {
      testWeightKgHl: unitConvert.convertTestweight(
        obj.defaultTestWeightKgHl > 0 ? obj.defaultTestWeightKgHl : 40,
        unitConvert.KG_HL,
        testWeightUnit,
      ),
      marketMoisture: obj.marketMoisture,
      cropId: obj.id,
      name: '',
    }
    return valuesToChange
  }

  const clearData = useCallback((obj) => {
    editObj.testWeightKgHl = unitConvert.convertTestweight(
      editObj.testWeightKgHl,
      testWeightUnit,
      unitConvert.KG_HL,
    )
    const newRows = rows.map((row) => {
      if (row.id === obj.id) {
        return { ...row, ...editObj }
      }
      return row
    })
    setRows(newRows)
    if (!addButtonClicked) {
      setSelectedRow(-1)
    } else {
      toggleAddRow(false)
      setRows(rows.filter((item) => item.id !== obj.id))
    }
  })

  const disableButton = useCallback(
    (data) => {
      const testWeight =
        !isNaN(data.testWeightKgHl) && parseFloat(data.testWeightKgHl) <= 0.0
      if (
        data.testWeightKgHl === '' ||
        data.marketMoisture === '' ||
        data.name === '' ||
        testWeight
      ) {
        return true
      }
      return false
    },
    [rows, cropVarieties],
  )

  const validate = (data) => {
    let temp = { ...errors }
    if ('name' in data) {
      temp.name = data.name ? '' : t('required_field')
    }
    if ('testWeightKgHl' in data) {
      temp.testWeightKgHl = data.testWeightKgHl > 0 ? '' : t('required_field')
    }
    if ('marketMoisture' in data) {
      temp.marketMoisture = data.marketMoisture > 0 ? '' : t('required_field')
    }
    setErrors({
      ...temp,
    })
  }

  const getCropCategoryName = useCallback((cropId) => {
    let crop = cropConstants.filter((item) => item.id === cropId)[0]
    return crop && crop.name ? getTranslatedName(crop.name) : ''
  })

  return (
    <>
      {/* <Box width={1} > */}
      <Box
        style={{ overflowX: 'auto' }}
        sx={{
          padding: '1rem',
          width: 1,
        }}
      >
        <h1>{t('crops_varieties')}</h1>
        {disableDeleteItem ? (
          <Alert severity="error">
            {t('delete_item_disable_message', { item: t('crop_variety') })}
          </Alert>
        ) : null}
        <Box
          sx={{
            my: 1,
            border: `1px solid ${colors.brand}`,
            borderRadius: '2px',
          }}
        >
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="left">{t('name')}</TableCell>
                  <TableCell align="left">{t('manufacturer')}</TableCell>
                  <TableCell align="left">{t('crop_category')}</TableCell>
                  <TableCell align="left">
                    {`${t('test_weight')}`}{' '}
                    <span
                      style={{ textTransform: 'lowercase' }}
                    >{`(${t(testWeightUnit)})`}</span>
                  </TableCell>
                  <TableCell align="left">{t('market_moisture')}</TableCell>
                  <TableCell align="left"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length
                  ? rows &&
                    rows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell component="th" scope="row">
                          {Object.keys(editObj).length &&
                          rowSelected === row.id ? (
                            <TextField
                              type="text"
                              name="name"
                              required
                              value={row?.name}
                              size="small"
                              disabled={row.id != rowSelected}
                              onChange={(e) => handleInputChange(e, row)}
                              {...(row.id == rowSelected && {
                                ...(errors['name'] && {
                                  error: true,
                                  helperText: errors['name'],
                                }),
                              })}
                            />
                          ) : (
                            getTranslatedName(row.name)
                          )}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {Object.keys(editObj).length &&
                          rowSelected === row.id ? (
                            <TextField
                              value={row.manufacturer || ''}
                              size="small"
                              name="manufacturer"
                              disabled={row.id != rowSelected}
                              onChange={(e) => handleInputChange(e, row)}
                            />
                          ) : row.manufacturer === '' || !row.manufacturer ? (
                            '-'
                          ) : (
                            row.manufacturer
                          )}
                        </TableCell>
                        <TableCell align="left">
                          {Object.keys(editObj).length &&
                          rowSelected === row.id ? (
                            <TextField
                              id="variety"
                              select
                              name="cropId"
                              variant="outlined"
                              size="small"
                              // placeholder={t('select_crop_variety')}
                              value={row.cropId}
                              disabled={row.id != rowSelected}
                              onChange={(e) => handleInputChange(e, row)}
                              slotProps={{
                                select: { native: true },
                              }}
                            >
                              {sortedCropConstants &&
                              Object.keys(sortedCropConstants) ? (
                                sortedCropConstants.map((item) => {
                                  return (
                                    <option value={item.id} key={item.id}>
                                      {getTranslatedName(item.name)}
                                    </option>
                                  )
                                })
                              ) : (
                                <></>
                              )}
                            </TextField>
                          ) : (
                            getCropCategoryName(row.cropId)
                          )}
                        </TableCell>

                        <TableCell component="th" scope="row">
                          {Object.keys(editObj).length &&
                          rowSelected === row.id ? (
                            <TextField
                              sx={{ width: '9ch' }}
                              type="number"
                              value={row.testWeightKgHl}
                              slotProps={{ htmlInput: { min: 0.01 } }}
                              size="small"
                              name="testWeightKgHl"
                              disabled={row.id != rowSelected}
                              onChange={(e) => handleInputChange(e, row)}
                              onBlur={(e) => handleOnBlur(e, row)}
                              {...(row.id == rowSelected && {
                                ...(errors['testWeightKgHl'] && {
                                  error: true,
                                  helperText: errors['testWeightKgHl'],
                                }),
                              })}
                            />
                          ) : unitConvert
                              .convertTestweight(
                                row.testWeightKgHl,
                                unitConvert.KG_HL,
                                testWeightUnit,
                              )
                              .toFixed(2) > 0 ? (
                            unitConvert
                              .convertTestweight(
                                row.testWeightKgHl,
                                unitConvert.KG_HL,
                                testWeightUnit,
                              )
                              .toFixed(2)
                          ) : (
                            40
                          )}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {Object.keys(editObj).length &&
                          rowSelected === row.id ? (
                            <TextField
                              sx={{ width: '9ch' }}
                              type="number"
                              value={row.marketMoisture || ''}
                              slotProps={{ htmlInput: { min: 0.0 } }}
                              name="marketMoisture"
                              size="small"
                              disabled={row.id != rowSelected}
                              onChange={(e) => handleInputChange(e, row)}
                              {...(row.id == rowSelected && {
                                ...(errors['marketMoisture'] && {
                                  error: true,
                                  helperText: errors['marketMoisture'],
                                }),
                              })}
                            />
                          ) : (
                            row.marketMoisture || ''
                          )}
                        </TableCell>

                        {canEdit && (
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{ borderBottom: 'none' }}
                          >
                            {!rowSelected || row.id != rowSelected ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                }}
                              >
                                <IconButton
                                  color="primary"
                                  aria-label={t('edit_crop_variety')}
                                  onClick={() => enableSelectedRow(row)}
                                  size="large"
                                >
                                  <ModeEditOutlineOutlinedIcon
                                    fontSize="small"
                                    style={{ color: colors.brandLight }}
                                  />
                                </IconButton>
                                <IconButton
                                  color="primary"
                                  aria-label={t('delete_crop_variety')}
                                  sx={iconButtonStyle}
                                  onClick={() => deleteVariety(row.id)}
                                  size="large"
                                >
                                  <DeleteOutlineOutlinedIcon
                                    fontSize="small"
                                    style={{ color: colors.dangerAccent }}
                                  />
                                </IconButton>
                              </Box>
                            ) : (
                              <div hidden={row.id != rowSelected}>
                                <IconButton
                                  color="primary"
                                  aria-label={t('save')}
                                  sx={iconButtonStyle}
                                  disabled={disableButton(row)}
                                  onClick={() => saveData(row)}
                                  size="large"
                                >
                                  <SaveOutlinedIcon
                                    fontSize="small"
                                    style={{ color: colors.brandLight }}
                                  />
                                </IconButton>
                                <IconButton
                                  color="secondary"
                                  aria-label={t('cancel')}
                                  sx={iconButtonStyle}
                                  onClick={() => clearData(row)}
                                  size="large"
                                >
                                  <ClearIcon
                                    fontSize="small"
                                    style={{ color: colors.dangerAccent }}
                                  />
                                </IconButton>
                              </div>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        {canEdit && (
          <Box
            sx={{
              display: 'flex',
            }}
          >
            <Button
              variant="outlined"
              aria-label={`${t('add')} ${t('crop_variety')} `}
              onClick={(e) => addEmptyRow(e)}
              sx={{ borderRadius: '25px', marginTop: '20px' }}
              endIcon={<AddIcon />}
            >
              {`${t('add')} ${t('crop_variety')} `}
            </Button>
          </Box>
        )}
      </Box>
    </>
  )
}
